/**
 * Pagination-komponentin puhdas logiikka (AB-WP6.4) — sivumäärä, sivun
 * clampaus, "X–Y of Z" -tekstin muodostus ja näytettävien sivunumeropainikkeiden
 * malli (ellipsis-ikkunointi). Ei DOMia — testattavissa suoraan; runtime.ts:n
 * wirePagination hoitaa piirron + slot-julkaisun näiden päälle.
 */
import { resolveMessage } from './messages';

/** Ellipsis-katko sivunumeroluettelossa. */
export const ELLIPSIS = 'ellipsis';
export type PageItem = number | typeof ELLIPSIS;

/** Sivujen kokonaismäärä; aina vähintään 1 (tyhjäkin näyttää sivun 1). */
export function pageCountOf(totalRows: number, pageSize: number): number {
  const total = Math.max(0, Math.floor(totalRows) || 0);
  const size = Math.max(1, Math.floor(pageSize) || 1);
  return Math.max(1, Math.ceil(total / size));
}

/** Rajaa sivu välille [1, pageCount]. */
export function clampPage(page: number, pageCount: number): number {
  const p = Math.floor(page) || 1;
  return Math.max(1, Math.min(p, Math.max(1, pageCount)));
}

/** 0-pohjainen rivioffset annetulle sivulle. */
export function offsetOf(page: number, pageSize: number): number {
  const size = Math.max(1, Math.floor(pageSize) || 1);
  return (Math.max(1, Math.floor(page) || 1) - 1) * size;
}

/** "X–Y of Z" -näyttöteksti (0 riviä → "0 of 0"). */
export function rangeText(page: number, pageSize: number, totalRows: number): string {
  const total = Math.max(0, Math.floor(totalRows) || 0);
  // WP-KL3 (löydös E-3): väliteksti tulee sanastosta, joten suomenkielisessä
  // Appissa lukee "1–10 / 120" eikä "1–10 of 120". Molemmat avaimet ovat myös
  // metadata.messages-ylikirjoitettavia kuten kaikki muutkin sisäänrakennetut.
  if (total <= 0) return resolveMessage('paginationEmpty');
  const size = Math.max(1, Math.floor(pageSize) || 1);
  const start = (Math.max(1, page) - 1) * size + 1;
  const end = Math.min(page * size, total);
  return resolveMessage('paginationRange')
    .replace('{start}', String(start))
    .replace('{end}', String(end))
    .replace('{total}', String(total));
}

/**
 * Näytettävät sivupainikkeet ellipsis-ikkunoinnilla. ≤7 sivua → kaikki;
 * muuten 1, current±1, viimeinen, ellipsikset väliin. Aina 1-indeksoituja.
 */
export function pageItems(page: number, pageCount: number): PageItem[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const items: PageItem[] = [1];
  const left = Math.max(2, page - 1);
  const right = Math.min(pageCount - 1, page + 1);
  if (left > 2) items.push(ELLIPSIS);
  for (let i = left; i <= right; i += 1) items.push(i);
  if (right < pageCount - 1) items.push(ELLIPSIS);
  items.push(pageCount);
  return items;
}
